import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import { buildQueryString } from '@/api/shared/queryBuilder';
import type { ApiResponse } from '@/api/shared/types';

import { mapAuditFilterItemToModel, mapAuditUserFilterItemToModel, mapAuditPagedResultToModel } from './audit.mapper';
import type {
  AuditExportHybridResult,
  AuditFilterOption,
  AuditRecordsPage,
  LC_AuditFilterItemOut,
  LC_AuditUserFilterItemOut,
  LC_ExportAuditRecordsIn,
  LC_ExportJobStatusOut,
  LC_ListAuditRecordsIn,
  LC_ListAuditRecordsOut,
} from './types';


export class AuditService {
  async getEntities(): Promise<ApiResponse<AuditFilterOption[]>> {
    const response = await apiClient.get<LC_AuditFilterItemOut[]>(
      API_CONFIG.ENDPOINTS.AUDIT.FILTERS_ENTITIES,
    );

    if (response.success && response.data) {
      return { ...response, data: response.data.map(mapAuditFilterItemToModel) };
    }

    return { ...response, data: null };
  }

  async getPrograms(): Promise<ApiResponse<AuditFilterOption[]>> {
    const response = await apiClient.get<LC_AuditFilterItemOut[]>(
      API_CONFIG.ENDPOINTS.AUDIT.FILTERS_PROGRAMS,
    );

    if (response.success && response.data) {
      return { ...response, data: response.data.map(mapAuditFilterItemToModel) };
    }

    return { ...response, data: null };
  }

  async getActions(): Promise<ApiResponse<AuditFilterOption[]>> {
    const response = await apiClient.get<LC_AuditFilterItemOut[]>(
      API_CONFIG.ENDPOINTS.AUDIT.FILTERS_ACTIONS,
    );

    if (response.success && response.data) {
      return { ...response, data: response.data.map(mapAuditFilterItemToModel) };
    }

    return { ...response, data: null };
  }

  async getUsers(): Promise<ApiResponse<AuditFilterOption[]>> {
    const response = await apiClient.get<LC_AuditUserFilterItemOut[]>(
      API_CONFIG.ENDPOINTS.AUDIT.FILTERS_USERS,
    );

    if (response.success && response.data) {
      return { ...response, data: response.data.map(mapAuditUserFilterItemToModel) };
    }

    return { ...response, data: null };
  }

  async getRecords(
    input: LC_ListAuditRecordsIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AuditRecordsPage>> {
    const queryString = buildQueryString({ ...input });

    const response = await apiClient.get<LC_ListAuditRecordsOut>(
      `${API_CONFIG.ENDPOINTS.AUDIT.RECORDS}${queryString}`,
      { signal },
    );

    if (response.success && response.data) {
      return { ...response, data: mapAuditPagedResultToModel(response.data) };
    }

    return { ...response, data: null };
  }

  async exportRecords(
    input: LC_ExportAuditRecordsIn,
  ): Promise<ApiResponse<AuditExportHybridResult>> {
    const queryString = buildQueryString({ ...input });
    return apiClient.downloadBlobOrAcceptJob(
      `${API_CONFIG.ENDPOINTS.AUDIT.RECORDS_EXPORT}${queryString}`,
      { method: 'GET' },
    );
  }

  async getExportJobStatus(jobId: string): Promise<ApiResponse<LC_ExportJobStatusOut>> {
    return apiClient.get<LC_ExportJobStatusOut>(
      API_CONFIG.ENDPOINTS.ASYNC_JOBS.STATUS(jobId),
    );
  }

  async downloadExportJobFile(
    jobId: string,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return apiClient.downloadBlobWithFilename(
      API_CONFIG.ENDPOINTS.ASYNC_JOBS.DOWNLOAD(jobId),
      { method: 'GET' },
    );
  }
}

export const auditService = new AuditService();
